import * as Haptics from 'expo-haptics';
import { router } from 'expo-router';
import { ArrowRight, Mail, Zap } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { Alert, Platform, Text, View } from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { HeroWatermark } from '@/components/HeroWatermark';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Skeleton } from '@/components/Skeleton';
import { Stack } from '@/components/Stack';
import { colors } from '@/constants/theme';
import { requestReviewOnce } from '@/lib/reviewPrompt';
import { authStore } from '@/stores/auth.store';
import { cashoutStore } from '@/stores/cashout.store';

import { BreakdownRow, styles } from '@/components/cashout/confirm-styles';

const CashoutConfirm = observer(() => {
  const user = authStore.user;
  const balanceCents = user?.balance_cents ?? 0;
  const isBelowMin = balanceCents < authStore.minimumCashoutCents;
  const hasEmail = !!user?.email;
  // Gate only on auth: the screen reads `user.balance_cents` (from /me), not
  // earnings. Gating on earningsStore.loaded hung a cold refresh forever —
  // nothing on this route loads earnings (only the Home tab does).
  const isLoading = !authStore.isAuthenticated;
  const dollars = (balanceCents / 100).toFixed(2);
  const centsUntilMin = authStore.minimumCashoutCents - balanceCents;

  const handleCashOut = () => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    const title = `Cash out $${dollars}?`;
    const message = "You'll receive an email with your reward link shortly.";

    if (Platform.OS === 'web') {
      if (!window.confirm(`${title}\n${message}`)) return;
      void submitCashout();
    } else {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Cash out', onPress: () => void submitCashout() },
      ]);
    }
  };

  const submitCashout = async () => {
    const id = await cashoutStore.create(balanceCents);
    if (id) {
      void requestReviewOnce();
      router.replace(`/cashout/${id}`);
    }
  };

  if (isLoading) {
    return (
      <Screen edges={['top', 'bottom']} testID="cashout-confirm-loading">
        <Stack gap="lg">
          <BackChevron />
          <Skeleton width="100%" height={240} radius="lg" />
          <Skeleton width="100%" height={56} radius="lg" />
          <Skeleton width="100%" height={48} radius="full" />
        </Stack>
      </Screen>
    );
  }

  return (
    <Screen edges={['top', 'bottom']} testID="cashout-confirm">
      <Stack gap="lg">
        <View style={styles.topBar}>
          <BackChevron />
          <Text style={styles.title}>Cash Out</Text>
          <View style={styles.titleSpacer} />
        </View>

        <Card variant="dark" style={styles.heroCard}>
          <HeroWatermark />
          <Overline tone="onDark" style={styles.payoutLabel}>
            TOTAL PAYOUT
          </Overline>
          <MoneyDisplay
            cents={balanceCents}
            size="hero"
            tone="onDark"
            showCents
          />
          <View style={styles.breakdown}>
            <BreakdownRow label="Amount" value={`$${dollars}`} />
            <BreakdownRow label="Estimated arrival" value="Instant to most methods" />
          </View>
        </Card>

        <View style={styles.infoBanner}>
          <Zap size={14} color={colors.deepGreen} />
          <Text style={styles.infoBannerText}>
            Choose Visa, Amazon, Venmo, or PayPal after you cash out.
          </Text>
        </View>

        {!hasEmail ? (
          <Card style={styles.emailGuard}>
            <View style={styles.emailGuardContent}>
              <Mail size={24} color={colors.textMuted} />
              <Text style={styles.emailGuardText}>
                Add your email to cash out
              </Text>
            </View>
            <Button
              label="Go to profile"
              variant="outline"
              size="sm"
              onPress={() => router.push('/(tabs)/profile')}
              accessibilityLabel="Go to profile to add email"
              testID="cashout-profile-link"
            />
          </Card>
        ) : (
          <>
            <Button
              label={`Cash out $${dollars}`}
              variant="primary"
              fullWidth
              trailingIcon={ArrowRight}
              disabled={isBelowMin}
              loading={cashoutStore.creating}
              onPress={handleCashOut}
              accessibilityLabel={`Cash out, $${dollars} available`}
              testID="cashout-submit"
            />
            {cashoutStore.createError && (
              <Text style={styles.errorText}>{cashoutStore.createError}</Text>
            )}
            {isBelowMin && (
              <Text style={styles.belowMinText}>
                ${(centsUntilMin / 100).toFixed(2)} more to cash out
              </Text>
            )}
          </>
        )}
      </Stack>
    </Screen>
  );
});

export default CashoutConfirm;
